import { AppCurrency } from "../config/currencies"
import { AppLocale } from "../config/locales"
import { DbSchema } from "../models"
import Imap from "imap"
import { MessagesDICT } from "../services/messages"
import SMTPTransport from "nodemailer/lib/smtp-transport"

export type FastifySchemeBase = {
    $id: string
}

export enum HistoryLogEntityType {
    SYSTEM = 0,
    USER = 1,
    COMPANY = 2,
    COMPANY_BUDGET = 3,
    USER_ROLE = 4,
    SUPPLIER = 5,
    PAYMENT_REQUEST = 6,
    INVOICE = 7,
    EVENT = 8,
    ARCHIVE = 9,
}

export enum AppStateKeyCode {
    VERSION = "version",
    IMAP_MAILBOX = "imap-mailbox",
    GOOGLE_MAILBOX = "google-mailbox",
    EXTRACT_INVOICES = "extract-invoices",
    EVENT_MAILING = "event-mailing",
    BANK_TRANSACTIONS_FETCH = "bank-transactions-fetch",
    INVOICE_VALIDATION = "invoice-validation",
    INVOICE_PAIRING = "invoice-pairing",
}

export enum HistoryLogOperation {
    CREATE = 1,
    UPDATE = 2,
    DELETE = 3,
    CHANGE_PASSWORD = 4,
    ASSIGN_ROLE = 5,
    UNASSIGN_ROLE = 6,
    UPLOAD_PROFILE_IMAGE = 7,
    CREATE_COMPANY_BUDGET = 8,
    PAYMENT_REQUEST_CHANGE_STATUS = 9,
    PAYMENT_REQUEST_ASSIGN_APPROVER = 10,
    PAYMENT_REQUEST_APPROVED = 11,
    COMMENT_CREATED = 12,
    PAYMENT_REQUEST_INVOICE_ASSIGNED = 13,
    INVOICE_UPLOAD_FILE = 14,
    INVOICE_UPLOAD_MANUAL_METADATA = 15,
    INVOICE_CHANGE_STATUS = 16,
    INVOICE_CHANGE_PAYMENT_REQUEST = 17,
    INVOICE_ASSIGNED_TO_COMPANY = 18,
    INVOICE_METADATA_AUTO_EXTRACTED = 19,
    INVOICE_METADATA_AUTO_EXTRACT_FAILED = 20,
    INVOICE_ASSIGNED_TO_SUPPLIER = 21,
    INVOICE_SET_AMOUNT = 22,
    INVOICE_SET_CURRENCY = 23,
    INVOICE_SET_DATE = 24,
    UPDATE_COMPANY_BUDGET = 25,
    DELETE_COMPANY_BUDGET = 26,
    USER_LOGIN = 27,
    PAYMENT_REQUEST_ATTACHMENT_UPLOADED = 28,
    INVOICE_CREATED_FROM_EMAIL = 29,
    EVENT_MARK_AS_READ = 30,
    PAYMENT_REQUEST_CHANGE_AMOUNT = 31,
    ARCHIVE_UPLOAD_FILE = 32,
    INVOICE_MAIL_IMPORT = 33,
    INVOICE_CHANGE_EXTERNAL_ID = 34,
    INVOICE_MANUALLY_SET_PAYMENT_REQUEST = 35,
    COMPANY_BANK_CERT_UPLOADED = 36,
    BANK_TRANSACTION_ASSIGNED_TO_INVOICE = 37,
    USER_PASSWORD_RECOVERED = 38,
    INVOICE_MARK_DUPLICATE = 39,
    INVOICE_CONFIRM_DUPLICATE = 40,
    INVOICE_REJECT_DUPLICATE = 41,
    INVOICE_RESTORE = 42,
    PAYMENT_REQUEST_RETURNED = 43,
    INVOICE_MOVETO_DEPOSIT = 44,
}

export enum InvoiceDuplicateResolution {
    CONFIRM = "confirm",
    NOT_DUPLICATE = "not-duplicate",
    RESTORE = "restore",
}

export enum UserRole {
    USER = "user", // uzivatel
    APPROVER = "approver", // schvalovatel
    SUBSTITUTE_APPROVER = "substitute-approver", // zastupujici schvalovatel
    USER_ADMINISTRATOR = "user-administrator", // spravce uzivatelu
    BILLER = "biller", // fakturant
    ACCOUNTANT = "accountant", // ucetni
    BUDGET_OFFICER = "budget-officer", // rozpoctar
    ADMIN = "admin", // administrator
    ARCHIVER = "archiver", // archivar
    MANAGER = "manager", // manazer
    DEPOSITOR = "depositor", // depositář
}

export enum EArchiveType {
    ORDERS = 1,
    INVOICES_INCOMING = 2,
    INVOICES_OUTGOING = 3,
    BANKS = 4,
    INTERNAL_DOCUMENTS = 5,
    CONTRACTS = 6,
    INVENTORIES = 7,
    DEBTS = 8,
    OBLIGATIONS = 9,
    OTHERS = 10,
}

export type PaymentRequestCategory = {
    id: number
    name: string
}

export enum PaymentRequestType {
    ONETIME = "oneTime",
    PERMANENT = "permanent",
}
export enum PaymentRequestFrequency {
    MONTHLY = "monthly",
    QUARTERLY = "quarterly",
    YEARLY = "yearly",
}
export enum PaymentRequestStatus {
    NEW = 1, // novy
    FORAPPROVAL = 2, // ke schvaleni
    APPROVED = 3, // schvaleno
    DENIED = 4, // zamitnuto
    CANCELED = 5, // zruseno / stornovano
    CLOSED = 6, // uzavreno
    RETURNED = 7, // vraceno k prepracovani
}

export enum NumberOperation {
    LT = "lt",
    LTE = "lte",
    EQ = "eq",
    GTE = "gte",
    GT = "gt",
}

export enum DateOperation {
    LT = "lt",
    LTE = "lte",
    EQ = "eq",
    GTE = "gte",
    GT = "gt",
}

export enum InvoiceStatus {
    FOREXTRACTION = 1, // k vytezeni
    FORMANUALEXTRACTION = 2, // k rucni extrakci
    EXTRACTED = 3, // vytezeno
    NOTASSSIGNED = 4, // neprirazeno
    TOBEACCOUNTED = 5, // k zauctovani
    ACCOUNTED = 6, // zauctovano
    PAID = 7, // zaplaceno
    REJECTED = 8, // zamitnuto
}

export enum EventName {
    VERSIONUPDATE = "version-update",
    INVOICE_AUTO_EXTRACT = "invoice-auto-extract",
    INVOICE_CREATED_FROM_EMAIL = "invoice-created-from-email",
    INVOICE_NOT_ASSIGNED = "invoice-not-assigned",
    INVOICE_MARKED_AS_DUPLICATE = "invoice-marked-as-duplicate",
    PAYMENT_REQUEST_APPROVER_ASSIGNED = "payment-request-approver-assigned", // obsolete
    PAYMENT_REQUEST_APPROVED = "payment-request-approved", 
    PAYMENT_REQUEST_DENIED = "payment-request-denied", 
    PAYMENT_REQUEST_CANCELED = "payment-request-canceled",
    PAYMENT_REQUEST_RETURNED = "payment-request-returned",
    TEST = "test",
}

export enum EventSeverity {
    ERROR = 3,
    WARNING = 2,
    SUCCESS = 1,
    INFO = 0,
}

export enum InvoiceType {
    MANUAL = "manual",
    MAIL = "mail",
}

export type InvoiceExtractedMetadata = {
    number?: string
    date?: Date
    dueDate?: Date
    uzpDate?: Date
    supplier: {
        id?: number
        name?: string
        address?: string
        ic?: string
        dic?: string
    }
    customer: {
        id?: number
        name?: string
        address?: string
        ic?: string
        dic?: string
    }
    currency?: AppCurrency
    totalPrice?: number
    discount?: number
    extractedKeywords?: string[]
    bankAccountIban?: string
    bankAccountNumber?: string
    bankTransactionId?: string
    items: InvoiceExtractedMetadataItem[]
}

export type InvoiceExtractedMetadataItem = {
    name?: string
    quantity?: number
    unitPrice?: number
    discount?: number
    price?: number
    vatPercent?: number
    vatPrice?: number
    total?: number
}

export type ServiceConfig = {
    version: string
    appUrl: string
    db: DbSchema
    aresApiUrl: string
    locale: AppLocale
    dataStorage: string
    azurePortalEndpoint: string
    azurePortalKey: string
    currency: AppCurrency
    superUserId: number
    messages: MessagesDICT
    smtpConfig?: SMTPTransport.Options
    smtpSender?: string
}

export type TasksConfig = ServiceConfig & {
    connectionString: string
    imapConfig?: Imap.Config
    rbClientId: string
    disableEvents?: boolean
}

export enum EBankCode {
    RAIFFEISEN = 5500,
}
